(() => {
  const storageKey = 'vh:collection-navigation:v1';
  const snapshotKey = 'vh:collection-snapshot:v1';
  const gridSelector = '[data-section-type="collection"] .collection__products';

  const read = () => {
    try {
      const state = JSON.parse(sessionStorage.getItem(storageKey));
      return state && Array.isArray(state.pages) && Number.isFinite(state.y) ? state : null;
    } catch (_) {
      return null;
    }
  };
  const write = state => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state));
      return true;
    } catch (_) {
      return false;
    }
  };
  const localUrl = value => {
    try {
      const url = new URL(value, location.href);
      return url.origin === location.origin ? url : null;
    } catch (_) {
      return null;
    }
  };
  const collectionUrl = value => {
    const url = localUrl(value);
    return url && /\/collections\/[^/]+(?:\/[^/]+)?\/?$/.test(url.pathname) ? url : null;
  };
  const productKey = value => {
    const url = localUrl(value);
    const handle = url?.pathname.match(/\/products\/([^/]+)/)?.[1];
    return handle ? `${handle}?variant=${url.searchParams.get('variant') || ''}` : null;
  };
  const pageKey = value => {
    const url = localUrl(value);
    if (!url) return null;
    url.hash = '';
    // The preview parameter is consumed by Shopify into a cookie on first load.
    url.searchParams.delete('preview_theme_id');
    url.searchParams.sort();
    return url.pathname + url.search;
  };

  const saved = read();
  const backNavigation = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
  const returning = saved && collectionUrl(location.href)
    && pageKey(saved.url) === pageKey(location.href)
    && (saved.restore || backNavigation);
  // Pause the existing infinite-scroll observer before it can request another page.
  window.vhCollectionRestoring = Boolean(returning);
  if (returning) history.scrollRestoration = 'manual';
  const restoredSnapshot = returning && restoreSnapshot();

  function saveSnapshot(grid, state) {
    try {
      sessionStorage.setItem(snapshotKey, JSON.stringify({
        id: state.id,
        theme: window.Shopify?.theme?.id,
        grid: grid.innerHTML,
        trigger: grid.closest('[data-section-type="collection"]')?.querySelector('infinite-scroll')?.outerHTML || ''
      }));
    } catch (_) {
      // Storage can be full or disabled. The URL/page-based fallback still works.
    }
  }

  function restoreSnapshot() {
    try {
      const snapshot = JSON.parse(sessionStorage.getItem(snapshotKey));
      const grid = document.querySelector(gridSelector);
      if (!grid || snapshot?.id !== saved.id || snapshot.theme !== window.Shopify?.theme?.id) return false;
      grid.innerHTML = snapshot.grid;
      grid.dataset.vhLoadedPages = JSON.stringify(saved.pages);
      const trigger = grid.closest('[data-section-type="collection"]')?.querySelector('infinite-scroll');
      if (trigger) {
        const template = document.createElement('template');
        template.innerHTML = snapshot.trigger;
        trigger.replaceWith(template.content);
      }
      // Restored cards were already revealed. Do not replay their entry animations.
      grid.closest('[data-section-type="collection"]').querySelectorAll('.animation-fade-up-reveal').forEach(card => {
        card.classList.add('animation-initialized');
        ['opacity', 'transform', 'translate', 'rotate', 'scale'].forEach(property => card.style.removeProperty(property));
      });
      return true;
    } catch (_) {
      return false;
    }
  }

  function rememberCollection(event) {
    const link = event.target.closest('a[href]');
    const grid = document.querySelector(gridSelector);
    if (!link || !grid || !grid.contains(link) || !collectionUrl(location.href)) return;
    const product = productKey(link.href);
    if (!product) return;

    const section = grid.closest('[data-section-type="collection"]');
    const card = link.closest('.product-item, .variant-item') || link;
    const state = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      savedAt: Date.now(),
      url: location.href,
      title: section?.dataset.collectionTitle || document.title.replace(/\s+[–—|]\s+.*$/, ''),
      product,
      link: link.href,
      x: window.scrollX,
      y: window.scrollY,
      offset: card.getBoundingClientRect().top,
      pages: JSON.parse(grid.dataset.vhLoadedPages || '[]'),
      historyLength: history.length,
      restore: false
    };
    history.replaceState({ ...history.state, vhCollectionVisit: state.id }, '');
    saveSnapshot(grid, state);
    write(state);
  }

  function setupProductBreadcrumb() {
    const link = document.querySelector('[data-collection-breadcrumb]');
    if (!link || !saved || productKey(location.href) !== saved.product) return;

    const referrer = collectionUrl(document.referrer);
    const sameVisit = history.state?.vhCollectionContext === saved.id;
    if (!sameVisit && (!referrer || pageKey(referrer.href) !== pageKey(saved.url))) return;
    if (!collectionUrl(saved.url)) return;

    link.href = saved.url;
    link.textContent = saved.title;
    link.classList.add('no-transition');
    history.replaceState({ ...history.state, vhCollectionContext: saved.id }, '');
    link.addEventListener('click', event => {
      write({ ...saved, savedAt: Date.now(), restore: true });
      if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
        && !link.hasAttribute('target') && referrer && pageKey(referrer.href) === pageKey(saved.url)
        && history.length === saved.historyLength + 1) {
        // Reuse the actual collection history entry, including its browser page cache.
        event.preventDefault();
        event.stopPropagation();
        history.back();
      }
    });
    link.addEventListener('auxclick', event => {
      if (event.button === 1) write({ ...saved, savedAt: Date.now(), restore: true });
    });
  }

  const frame = () => new Promise(resolve => requestAnimationFrame(resolve));

  async function restorePosition(card) {
    let interrupted = false;
    const interrupt = () => { interrupted = true; };
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    events.forEach(event => window.addEventListener(event, interrupt, { passive: true }));
    try {
      const started = performance.now();
      let stableSince = started;
      do {
        const desiredY = card ? window.scrollY + card.getBoundingClientRect().top - saved.offset : saved.y;
        const y = Math.max(0, Math.min(desiredY, document.documentElement.scrollHeight - window.innerHeight));
        if (Math.abs(window.scrollY - y) > 1) {
          window.scrollTo({ left: saved.x, top: Math.max(0, y), behavior: 'instant' });
          stableSince = performance.now();
        }
        // Lazy images and the theme's reveal animations can still change row heights.
        await new Promise(resolve => setTimeout(resolve, 50));
      } while (!interrupted && performance.now() - started < 30000
        && (document.readyState !== 'complete' || performance.now() - stableSince < 1500));
    } finally {
      events.forEach(event => window.removeEventListener(event, interrupt));
    }
  }

  async function restoreCollection() {
    if (!returning) return;
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    try {
      // If browser storage was unavailable, fetch pages together instead of serially.
      const pages = restoredSnapshot ? [] : saved.pages.filter(page => collectionUrl(page));
      const responses = new Map();
      for (let index = 0; index < pages.length; index += 4) {
        await Promise.all(pages.slice(index, index + 4).map(async page => {
          try {
            const response = await fetch(page, { signal: AbortSignal.timeout(15000) });
            if (response.ok) responses.set(page, await response.text());
          } catch (_) { /* A failed page falls back to the available collection content. */ }
        }));
      }
      for (const page of pages) {
        const grid = document.querySelector(gridSelector);
        const loadedPages = JSON.parse(grid?.dataset.vhLoadedPages || '[]');
        if (loadedPages.some(loaded => pageKey(loaded) === pageKey(page))) continue;
        const trigger = grid?.closest('[data-section-type="collection"]')?.querySelector('infinite-scroll');
        if (!trigger || !trigger.anchor || !collectionUrl(page)) break;
        if (pageKey(trigger.anchor.href) !== pageKey(page)) break;
        if (!responses.has(page)) break;
        const loaded = await trigger.loadNextPage({ html: responses.get(page) });
        if (!loaded) break;
      }

      await frame();
      await frame();
      const grid = document.querySelector(gridSelector);
      const link = [...(grid?.querySelectorAll('a[href]') || [])]
        .find(anchor => productKey(anchor.href) === saved.product);
      const card = link?.closest('.product-item, .variant-item') || link;
      // Correct later layout shifts without blocking collection readiness or scrolling.
      void restorePosition(card);
    } catch (error) {
      console.error('Collection position could not be fully restored:', error);
      window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
    } finally {
      if (read()?.id === saved.id) write({ ...saved, restore: false });
      history.scrollRestoration = 'auto';
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
      window.vhCollectionRestoring = false;
      // Re-arm the observer after replay, including when its first callback was paused.
      const trigger = document.querySelector('infinite-scroll');
      if (trigger?.observer) {
        trigger.observer.unobserve(trigger);
        trigger.observer.observe(trigger);
      }
      window.dispatchEvent(new CustomEvent('vh:collection-restored'));
    }
  }

  document.addEventListener('click', rememberCollection, true);
  document.addEventListener('auxclick', event => {
    if (event.button === 1) rememberCollection(event);
  }, true);
  const ready = () => {
    void restoreCollection();
  };
  if (restoredSnapshot) {
    const initializeCards = () => window.dispatchEvent(new CustomEvent('products:loaded'));
    if (document.readyState === 'complete') initializeCards();
    else document.addEventListener('DOMContentLoaded', initializeCards, { once: true });
  }
  // A deferred script already sees the markup; do not wait for unrelated app scripts.
  setupProductBreadcrumb();
  if (restoredSnapshot) ready();
  else if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();

  // A page returned from the browser's cache already retains its loaded cards.
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      window.vhCollectionRestoring = false;
      const state = read();
      if (state && pageKey(state.url) === pageKey(location.href)) write({ ...state, restore: false });
    }
  });
})();
